(function () {
  'use strict';

  var WRAPPER_SELECTOR = '[data-product-card-video]';
  var PRELOAD_MARGIN = '400px 0px';
  var PLAY_MARGIN = '150px 0px';
  var KEEP_ALIVE_MARGIN = '1500px 0px';
  var preloadObserver = null;

  var playObserver = null;
  var keepAliveObserver = null;
  var autoplayUnlocked = false;

  /* ---------------------------------------------------------------- guards */

  if (!('IntersectionObserver' in window)) {
    return; // Card keeps its image behaviour.
  }

  function matches(query) {
    return !!(window.matchMedia && window.matchMedia(query).matches);
  }

  function prefersLessData() {
    var connection =
      navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    if (!connection) return false;
    if (connection.saveData === true) return true;

    var effectiveType = connection.effectiveType || '';
    return effectiveType === '2g' || effectiveType === 'slow-2g';
  }

  if (matches('(prefers-reduced-motion: reduce)') || prefersLessData()) {
    return; // Card keeps its image behaviour.
  }

  var canHover = matches('(hover: hover)');

  /* --------------------------------------------------------------- helpers */

  function getVideo(wrapper) {
    return wrapper.querySelector('video');
  }

  function isPackshotMode(wrapper) {
    return !!(wrapper.closest && wrapper.closest('.inverse'));
  }

  function attachSource(video) {
    if (video.getAttribute('data-loaded') === 'true') return;

    var src = video.getAttribute('data-video-src');
    if (!src) return;

    video.setAttribute('data-loaded', 'true');
    video.setAttribute('preload', 'auto');
    video.src = src;
    video.load();
  }

  /* A fast scroll can carry many cards across the wide 400px preload margin and back out past
     the 1500px keep-alive margin within the same gesture - each one individually correct
     (attach then release), but together a burst of real "start full video download, abort it"
     cycles across many videos in a couple of seconds. Not a leak, just legitimate churn at a rate
     the media pipeline may not absorb gracefully. Debouncing the preload trigger so a card only
     actually attaches its source after it's stayed in the preload zone for a moment means a card
     the user merely scrolls past never starts a real download at all. */
  var PRELOAD_DWELL_MS = 200;
  var pendingPreload = new WeakMap();

  function schedulePreload(video) {
    if (video.getAttribute('data-loaded') === 'true' || pendingPreload.has(video)) return;

    var timeoutId = setTimeout(function () {
      pendingPreload.delete(video);
      attachSource(video);
    }, PRELOAD_DWELL_MS);

    pendingPreload.set(video, timeoutId);
  }

  function cancelPendingPreload(video) {
    var timeoutId = pendingPreload.get(video);
    if (timeoutId) {
      clearTimeout(timeoutId);
      pendingPreload.delete(video);
    }
  }

  function releaseSource(wrapper) {
    var video = getVideo(wrapper);
    if (!video) return;

    cancelPendingPreload(video);

    if (video.getAttribute('data-loaded') !== 'true') return;

    video.pause();
    video.removeAttribute('src');
    video.setAttribute('preload', 'none');
    video.setAttribute('data-loaded', 'false');
    wrapper.classList.remove('is-playing');

    try {
      video.load(); // Frees the buffered media.
    } catch (error) {
      /* no-op */
    }
  }


  function shouldPlay(wrapper) {
    if (document.hidden) return false;
    if (wrapper.getAttribute('data-video-failed') === 'true') return false;
    if (wrapper.getAttribute('data-in-view') !== 'true') return false;

    // Packshot mode: packshot at rest, video on hover — the mirror of the
    // normal mode, where the video is at rest and the packshot is on hover.
    if (isPackshotMode(wrapper)) {
      return wrapper.getAttribute('data-hovered') === 'true';
    }

    return true;
  }

  function update(wrapper) {
    var video = getVideo(wrapper);
    if (!video) return;

    if (shouldPlay(wrapper)) {
      attachSource(video);

      video.muted = true;
      video.defaultMuted = true;

      var attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(function () {
          // Autoplay refused (e.g. Low Power Mode): keep showing the image.
          wrapper.classList.remove('is-playing');
        });
      }

      return;
    }

    video.pause();

    if (
      canHover &&
      isPackshotMode(wrapper) &&
      wrapper.getAttribute('data-in-view') === 'true' &&
      wrapper.getAttribute('data-video-failed') !== 'true'
    ) {
      attachSource(video);
      wrapper.classList.remove('is-playing');
    }
  }

  function updateAllInView() {
    var wrappers = document.querySelectorAll(WRAPPER_SELECTOR + '[data-in-view="true"]');
    for (var i = 0; i < wrappers.length; i++) update(wrappers[i]);
  }

  /* ------------------------------------------------------------- observers */

  function handleVisibility(entries) {
    for (var i = 0; i < entries.length; i++) {
      var wrapper = entries[i].target;

      if (entries[i].isIntersecting) {
        wrapper.setAttribute('data-in-view', 'true');
      } else {
        wrapper.removeAttribute('data-in-view');
      }

      update(wrapper);
    }
  }

  function handleKeepAlive(entries) {
    for (var i = 0; i < entries.length; i++) {
      if (!entries[i].isIntersecting) releaseSource(entries[i].target);
    }
  }
  function handlePreload(entries) {
    for (var i = 0; i < entries.length; i++) {
      if (!entries[i].isIntersecting) continue;

      var wrapper = entries[i].target;
      if (wrapper.getAttribute('data-video-failed') === 'true') continue;

      // In packshot mode on a touch device the video can never be revealed,
      // so there is nothing to buffer.
      if (isPackshotMode(wrapper) && !canHover) continue;

      var video = getVideo(wrapper);
      if (video) schedulePreload(video);
    }
  }

  /* The observers fire asynchronously, one frame or more after layout. For the
     cards already on screen at load, start the download immediately instead. */
  function primeFirstViewport() {
    var wrappers = document.querySelectorAll(WRAPPER_SELECTOR);
    var limit = (window.innerHeight || 0) + 400;

    for (var i = 0; i < wrappers.length; i++) {
      var rect = wrappers[i].getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > limit) continue;

      var video = getVideo(wrappers[i]);
      if (video) attachSource(video);
    }
  }
  /* ------------------------------------------------------------------ init */

  function setup(wrapper) {
    if (wrapper.getAttribute('data-video-ready') === 'true') return;

    var video = getVideo(wrapper);
    if (!video) return;

    wrapper.setAttribute('data-video-ready', 'true');
    video.setAttribute('data-loaded', 'false');
    video.muted = true;

    video.addEventListener('playing', function () {
      autoplayUnlocked = true;
      wrapper.classList.add('is-playing');
    });

    video.addEventListener('error', function () {
      if (!video.getAttribute('src')) return;

      wrapper.setAttribute('data-video-failed', 'true');
      wrapper.classList.remove('is-playing');
      preloadObserver.unobserve(wrapper);
      playObserver.unobserve(wrapper);
      keepAliveObserver.unobserve(wrapper);
    });

    if (canHover) {
      var card = wrapper.closest('.product-item') || wrapper.parentNode;

      card.addEventListener('mouseenter', function () {
        wrapper.setAttribute('data-hovered', 'true');
        update(wrapper);
      });

      card.addEventListener('mouseleave', function () {
        wrapper.removeAttribute('data-hovered');
        update(wrapper);
      });
    }

    preloadObserver.observe(wrapper);
    playObserver.observe(wrapper);
    keepAliveObserver.observe(wrapper);
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var wrappers = scope.querySelectorAll(WRAPPER_SELECTOR + ':not([data-video-ready])');

    for (var i = 0; i < wrappers.length; i++) setup(wrappers[i]);
  }

  function watchForNewCards() {
    if (!('MutationObserver' in window)) return;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;

        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;

          if (node.matches && node.matches(WRAPPER_SELECTOR)) {
            setup(node);
          } else {
            init(node);
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }
  var GESTURES = ['pointerdown', 'touchstart', 'click', 'keydown'];

  function unlockAutoplay() {
    if (autoplayUnlocked) {
      removeGestureListeners();
      return;
    }

    updateAllInView();
  }

  function removeGestureListeners() {
    for (var i = 0; i < GESTURES.length; i++) {
      document.removeEventListener(GESTURES[i], unlockAutoplay, true);
    }
  }

  function listenForGestures() {
    for (var i = 0; i < GESTURES.length; i++) {
      document.addEventListener(GESTURES[i], unlockAutoplay, true);
    }
  }

  function start() {
    preloadObserver = new IntersectionObserver(handlePreload, {
      rootMargin: PRELOAD_MARGIN,
      threshold: 0
    });
    playObserver = new IntersectionObserver(handleVisibility, {
      rootMargin: PLAY_MARGIN,
      threshold: 0.01
    });

    keepAliveObserver = new IntersectionObserver(handleKeepAlive, {
      rootMargin: KEEP_ALIVE_MARGIN,
      threshold: 0
    });

    init(document);
    primeFirstViewport();
    watchForNewCards(); // "Load more", filtering, sorting, quick view...
    listenForGestures();

    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });

    document.addEventListener('visibilitychange', updateAllInView);

    // Back from bfcache: the videos are paused and need restarting.
    window.addEventListener('pageshow', updateAllInView);

    // Packshot switch flipped: re-evaluate every visible card.
    document.addEventListener('change', function (event) {
      var target = event.target;
      if (!target || target.id !== 'inverse-packshots') return;

      // Let the theme toggle `.inverse` on the grid first.
      window.setTimeout(updateAllInView, 0);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();